import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { tap, Observable, BehaviorSubject, combineLatest, map, filter } from 'rxjs';
import { Angulartics2 } from 'angulartics2';

import { Account } from '@shared/account/models';
import { Location } from '@shared/location/models';
import { AuthToken, AuthUser } from '@shared/signup/models';
import { makeMap } from '@shared/core/functions';
import { SelectedLocation } from './models';
import {
  AuthAccountService,
  AuthLocationService,
  AuthRestaurantService,
} from '@tablesready/frontend-store';
import {
  AuthService,
  LastLocationIdService,
  TokenService,
} from '@tablesready/frontend-auth';

export interface AccountWithLocation extends Account {
  name: string;
  locations: {
    _id: string;
    name: string;
  }[];
}

@Injectable({ providedIn: 'root' })
export class LocationSwitcherService {
  accounts$ = new BehaviorSubject<AccountWithLocation[]>([]);
  selectedLocation$ = new BehaviorSubject<SelectedLocation>(null);

  constructor(
    private readonly router: Router,
    private readonly accountService: AuthAccountService,
    private readonly locationService: AuthLocationService,
    private readonly tokenService: TokenService,
    private readonly lastLocationIdService: LastLocationIdService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly authService: AuthService,
    private readonly angulartics2: Angulartics2,
  ) {}

  // TODO: think how to replace logic to get current active location with AuthRestaurantService
  initData(): Observable<[AuthUser, AuthToken[], Account[], Location[]]> {
    return combineLatest([
      this.authRestaurantService.select(),
      this.tokenService.tokens$
        .pipe(
          filter(tokens => !!tokens),
          map(tokens => tokens.filter(token => !!token.locationId)),
        ),
      this.accountService.selectAll(),
      this.locationService.selectAllEnabled(),
    ])
      .pipe(
        tap(([user, tokens, accounts, locations]) => {
          if (!this.authService.isAuthenticated()) {
            return;
          }

          const tokenMap = makeMap<AuthToken>(tokens, 'locationId');
          locations = locations.filter(({_id}) => tokenMap[_id]);
          if (locations.length) {
            const accountsWithLocations = accounts.map(account => {
              const accountLocations = locations.filter(location => location.accountId === account.id);

              return { ...account, locations: accountLocations };
            });
            this.accounts$.next(accountsWithLocations.filter(account => account.locations.length));
          } else {
            this.accounts$.next([]);
          }

          if (user.location) {
            const currentAccount = this.accountService.getById(user.location.accountId);
            const {name: locationName, _id: locationId} = user.location;
            this.selectedLocation$.next({account: currentAccount.name, locationName, locationId});
          } else {
            this.selectedLocation$.next(null);
          }
        })
      );
  }

  navigateToLocationPage(locationId: string, url: string): void {
    this.selectLocation(locationId);

    void this.router.navigateByUrl(url);
  }

  selectLocation(locationId: string, force = false): void {
    if (!force && this.lastLocationIdService.get() === locationId) {
      return;
    }

    const prevLocationName = this.selectedLocation$.value?.locationName;

    this.lastLocationIdService.set(locationId);
    this.tokenService.setActiveByLocationId(locationId);
    this.authRestaurantService.buildOnce().subscribe();

    const location = this.locationService.getById(locationId);
    const account = this.accountService.getById(location.accountId);
    this.selectedLocation$.next({account: account.name, locationName: location.name, locationId});
    this.angulartics2.eventTrack.next({
      action: 'switched location',
      properties: {
        category: 'location',
        gtmCustom: {
          accountName: account.name,
          switchedFrom: prevLocationName,
          switchedTo: location.name,
        },
      },
    });
  }
}
